import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { fetchRostersForGroups } from 'actions/roleActions';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import ContactMethodEditor from 'components/ProfileRoute/ContactMethodEditor';
import {
  ContactMethodDrafts,
  draftErrors,
  grantsNewSmsConsent,
  toContactMethodInputs,
  toDrafts,
} from 'components/ProfileRoute/contactMethodDraft';
import { useUpdateHomeAor } from 'hooks/useUpdateHomeAor';
import { useUpdateUserProfileSettingsMutation } from 'hooks/useUpdateUserProfileSettings';
import { AreaOfResponsibility } from 'types/aor';
import { useAorText } from 'utils/aorTerminology';
import { describeChannel } from 'utils/contactMethods';
import { homeAorSelectValue, HomeAorOption } from './utils/memberHomeAor';

type SelectOption = HomeAorOption;

const EDITABLE_CHANNELS: ContactChannel[] = ['sms', 'voice', 'pager'];

type Props = {
  row: RosterItem;
  canEdit: boolean;
  aors: AreaOfResponsibility[];
  groupGuid: string;
  onEditingChange?: (editing: boolean) => void;
};

function memberName(row: RosterItem): string {
  if (row.user_name) return row.user_name;
  if ('userName' in row.profile_settings && row.profile_settings.userName) {
    return row.profile_settings.userName;
  }
  return '';
}

function saveErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: unknown } } })
    ?.response?.data?.error;
  if (typeof data === 'string' && data.length > 0) return data;
  return 'Could not update this member.';
}

/**
 * Name, phone channels, and Home AOR for one member.
 *
 * An Org Admin edits them here. Email stays the login address. Email contact
 * methods stay in the save payload so editing text, call, and pager does not
 * delete them.
 */
const MemberContactDetails: React.FC<Props> = ({
  row,
  canEdit,
  aors,
  groupGuid,
  onEditingChange,
}) => {
  const aorText = useAorText();
  const dispatch = useDispatch();
  const updateProfile = useUpdateUserProfileSettingsMutation();
  const updateHomeAor = useUpdateHomeAor();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [contacts, setContacts] = useState<ContactMethodDrafts>(toDrafts([]));
  const [homeAor, setHomeAor] = useState<SelectOption | null>(null);

  const aorOptions = aors
    .filter((aor) => Boolean(aor.item_id))
    .map((aor) => ({ label: aor.name, value: aor.item_id as string }));

  const contactErrors = draftErrors(contacts);
  const willSendWelcomeText = grantsNewSmsConsent(
    contacts,
    row.contact_methods
  );
  const isSaving = updateProfile.isPending || updateHomeAor.isPending;

  const startEditing = () => {
    setName(memberName(row));
    setContacts(toDrafts(row.contact_methods));
    setHomeAor(homeAorSelectValue(aors, row.home_aor));
    setIsEditing(true);
    onEditingChange?.(true);
  };

  const save = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Enter a name.');
      return;
    }
    if (contactErrors.length > 0) return;

    try {
      await updateProfile.mutateAsync({
        userId: row.user_guid,
        newSettings: {
          ...(row.profile_settings ?? {}),
          userName: trimmedName,
        },
        contactMethods: toContactMethodInputs(contacts),
      });
      await updateHomeAor.mutateAsync({
        homeAor: homeAor?.value || null,
        userGuid: row.user_guid,
      });
      setIsEditing(false);
      onEditingChange?.(false);
      toast.success('Member updated');
    } catch (error) {
      toast.error(saveErrorMessage(error));
    } finally {
      dispatch(fetchRostersForGroups([groupGuid]));
    }
  };

  const homeAorName = row.home_aor
    ? aors.find(
        (aor) => aor.id === row.home_aor || aor.item_id === row.home_aor
      )?.name || row.home_aor
    : '';

  return (
    <>
      <div className="d-flex align-items-center justify-content-between border-bottom tw-border-b-neutral-700 tw-mb-3">
        <strong className="py-1">Contact</strong>
        {canEdit ? (
          <button
            type="button"
            className="button button--link"
            onClick={() => {
              if (isEditing) {
                setIsEditing(false);
                onEditingChange?.(false);
                return;
              }
              startEditing();
            }}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
        ) : null}
      </div>
      {isEditing ? (
        <>
          <div className="mb-3">
            <label className="form-label d-block mb-0">Name</label>
            <StylishNewInput
              value={name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setName(event.target.value)
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label d-block mb-0">Email</label>
            <span className="tw-ps-3 fst-italic">{row.email_address}</span>
          </div>
          {EDITABLE_CHANNELS.map((channel) => (
            <ContactMethodEditor
              key={channel}
              channel={channel}
              methods={contacts[channel]}
              onChange={(methods) =>
                setContacts((prev) => ({ ...prev, [channel]: methods }))
              }
              disabled={isSaving}
            />
          ))}
          <div className="mb-3">
            <label className="form-label d-block mb-0">
              {aorText('Home AOR')}
            </label>
            <StylishNewSelect
              options={aorOptions}
              value={homeAor}
              onChange={(next: SelectOption | null) => setHomeAor(next)}
              placeholder={aorText('Select Home AOR')}
              isClearable
              isSearchable
              isMulti={false}
              isDisabled={isSaving}
            />
          </div>
          {willSendWelcomeText ? (
            <div className="form-text mb-3">
              A confirmation text will be sent to each newly consented number.
            </div>
          ) : null}
          {contactErrors.map((error) => (
            <span
              className="form-text form-error multiline d-block mt-1"
              key={error}
            >
              {error}
            </span>
          ))}
          <StylishNewButton
            className="button--primary"
            type="button"
            disabled={isSaving || contactErrors.length > 0}
            onClick={save}
          >
            Save
          </StylishNewButton>
        </>
      ) : (
        <>
          <div className="mb-3">
            <label className="form-label d-block mb-0">Email</label>
            <span className="tw-ps-3 fst-italic">{row.email_address}</span>
          </div>
          <div className="mb-3">
            <label className="form-label d-block mb-0">Text</label>
            <span className="tw-ps-3 fst-italic">
              {describeChannel(row.contact_methods, 'sms')}
            </span>
          </div>
          <div className="mb-3">
            <label className="form-label d-block mb-0">Call</label>
            <span className="tw-ps-3 fst-italic">
              {describeChannel(row.contact_methods, 'voice')}
            </span>
          </div>
          <div className="mb-3">
            <label className="form-label d-block mb-0">Pager</label>
            <span className="tw-ps-3 fst-italic">
              {describeChannel(row.contact_methods, 'pager')}
            </span>
          </div>
          <div className="mb-3">
            <label className="form-label d-block mb-0">
              {aorText('Home AOR')}
            </label>
            <span className="tw-ps-3 fst-italic">{homeAorName}</span>
          </div>
        </>
      )}
    </>
  );
};

export default MemberContactDetails;
